import cookie from "react-cookies";

export const saveUserCookies = ({
  accessToken,
  refreshToken,
  userId,
}: {
  accessToken?: string;
  refreshToken?: string;
  userId?: string;
}) => {
  cookie.remove("noti_not_match_address");
  accessToken && cookie.save("access_token", accessToken, { path: "/" });
  refreshToken && cookie.save("refresh_token", refreshToken, { path: "/" });
  userId && cookie.save("user_id", userId, { path: "/" });
  // Tracking google login
};

interface IItemLocalStorage {
  key: string;
  value: string;
}

export function findLocalItems(query: {
  [Symbol.match](string: string): RegExpMatchArray | null;
}): IItemLocalStorage[] {
  let i;
  const results = [];
  for (i in localStorage) {
    // eslint-disable-next-line no-prototype-builtins
    if (localStorage.hasOwnProperty(i)) {
      if (i.match(query) || (!query && typeof i === "string")) {
        const value = JSON.parse(localStorage.getItem(i));
        results.push({ key: i, value });
      }
    }
  }
  return results;
}

async function deriveKey(
  password: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const encodedPassword = new TextEncoder().encode(password);

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encodedPassword,
    { name: "PBKDF2" },
    false,

    ["deriveBits", "deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 100000, // Adjust the number of iterations as needed for your security requirements
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

export async function decryptString(
  encryptedText: string,
  password: string
): Promise<string> {
  // Convert the Base64-encoded string to a Uint8Array
  const combined = new Uint8Array(
    atob(encryptedText)
      .split("")
      .map((c) => c.charCodeAt(0))
  );

  // Extract IV from the combined array
  const salt = combined.slice(0, 16);
  const iv = combined.slice(16, 28);
  const ciphertext = combined.slice(28);

  const derivedKey = await deriveKey(password, salt);

  // Decrypt using AES-GCM
  const decryptedArrayBuffer = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv,
    },
    derivedKey,
    ciphertext
  );

  // Convert the decrypted ArrayBuffer to a string
  return new TextDecoder().decode(decryptedArrayBuffer);
}

let resendTimeoutId = null;

export const setResendTimeout = (
  start: () => void,
  end: () => void,
  timeout = 30000
) => {
  start();
  resendTimeoutId = setTimeout(() => {
    end();
  }, timeout);
};

export const clearResendTimeout = () => {
  if (resendTimeoutId) {
    clearTimeout(resendTimeoutId);
    resendTimeoutId = null;
  }
};

export function getExpireDate(expireInMinutes: number) {
  const now = new Date();
  const expireTime = new Date(now);
  expireTime.setMinutes(now.getMinutes() + expireInMinutes);
  return expireTime;
}

export const compactAddress = (address = "", number = 4) => {
  return `${address.slice(0, number)}...${address.slice(-number)}`;
};

export const numberToHex = (number: number | string) =>
  `0x${Number(number)?.toString(16)}`;

export function formatCurrency(str: string | number, decimal = 2) {
  if (typeof str === "string") {
    return (
      parseFloat(str)?.toLocaleString("vi-VN", {
        maximumFractionDigits: decimal,
      }) || 0
    );
  } else {
    return (
      str?.toLocaleString("vi-VN", {
        maximumFractionDigits: decimal,
      }) || 0
    );
  }
}
export function convertEngToVie(str: string): string {
  const messages: Record<string, string> = {
    "you are not the folderowner": "Bạn không phải là chủ sở hữu thư mục.",
    "invalid size, need same number of list":
      "Dữ liệu không hợp lệ: số lượng phần tử trong danh sách phải bằng nhau.",
    "file exist!": "Tài liệu này đã tồn tại trên hệ thống.",
    "invalid name": "Tên không hợp lệ, vui lòng kiểm tra lại.",
    "same folder name": "Đã tồn tại thư mục có cùng tên trên hệ thống.",
    "invalid hash": "Hash không hợp lệ, vui lòng kiểm tra lại.",
    "User rejected the request.": "Người dùng đã từ chối yêu cầu.",
  };

  const normalizedStr = str.trim().toLowerCase();
  return messages[normalizedStr] || str;
}

export function formatRpcError(error: unknown): string {
  if (!error) return "Unknown error";

  if (error instanceof Error) {
    if (
      "cause" in error &&
      typeof error.cause === "object" &&
      error.cause &&
      "reason" in error.cause &&
      typeof error.cause.reason === "string"
    ) {
      return convertEngToVie(error.cause.reason);
    }

    // fallback lấy shortMessage
    if ("shortMessage" in error && typeof error.shortMessage === "string") {
      return convertEngToVie(error.shortMessage);
    }

    // fallback message
    return convertEngToVie(error.message) || "Unknown error";
  }

  return JSON.stringify(error);
}
